import { CircleCheck } from "lucide-react";
import React from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDivider,
  IgPaper,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  BaseIntegrationDto,
  GoogleGlobalSettingsDto,
} from "@intelligems/ig-types";
import { Box, Grid, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useGoogleLogin } from "../../api/google/google-login";
import { usePutGaIntegration } from "../../api/google/put-ga-integration";
import {
  gaKeys,
  integrationKeys,
  organizationsKeys,
} from "../../api/queryKeys";
import { GoogleAnalyticsStatusSection } from "../../domains/Settings/Integrations/Google/GoogleAnalyticsStatusSection";
import { SignInWithGoogle } from "../../images/Icons/SignInWithGoogle";

import { GoogleAnalyticsDisclaimer } from "./GoogleDisclamer";

interface GoogleAnalyticsProps {
  googleIntegration?: BaseIntegrationDto<GoogleGlobalSettingsDto>;
}

export const SetupGoogleIntegration = ({
  googleIntegration,
}: GoogleAnalyticsProps) => {
  const postGoogleLogin = useGoogleLogin();
  const putGaIntegration = usePutGaIntegration();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [globalSettings, setGlobalSettings] = useImmer<GoogleGlobalSettingsDto>(
    googleIntegration?.globalSettings || {
      ga: { accountId: "", propertyId: "", streamId: "", measurementId: "" },
    }
  );

  const onEnableClick = () => {
    postGoogleLogin.mutate(
      {},
      {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSuccess: async () => {
          await invalidate();
        },
      }
    );
  };

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    await queryClient.invalidateQueries({ queryKey: gaKeys.all });
    await queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
    setGlobalSettings({
      ga: {
        accountId: "",
        propertyId: "",
        streamId: "",
        measurementId: "",
      },
    });
  };

  const onSaveClick = () => {
    putGaIntegration.mutate(
      {
        body: {
          globalSettings: {
            ga: {
              accountId: globalSettings.ga?.accountId ?? "",
              propertyId: globalSettings.ga?.propertyId ?? "",
              streamId: globalSettings.ga?.streamId ?? "",
              measurementId: globalSettings.ga?.measurementId ?? "",
            },
          },
        },
        pathParams: undefined,
        queryParams: undefined,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSuccess: async () => {
          await invalidate();
        },
      }
    );
  };

  const onResetClick = () => {
    putGaIntegration.mutate(
      {
        body: {
          globalSettings: {
            ga: {
              accountId: "",
              propertyId: "",
              streamId: "",
              measurementId: "",
            },
          },
        },
        pathParams: undefined,
        queryParams: undefined,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSuccess: async () => {
          await invalidate();
          postGoogleLogin.mutate({});
        },
      }
    );
  };

  return (
    <IgPaper sx={{ boxShadow: "none" }}>
      <Grid item container direction={"column"}>
        <Grid
          item
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item>
            <Grid container item columnSpacing={2}>
              {googleIntegration?.enabled ? (
                <Grid item>
                  <CircleCheck size={24} color={theme.igIconColors.success} />{" "}
                </Grid>
              ) : null}

              <Grid item>
                <IgTypography
                  variant={"h3"}
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  Google Analytics 4
                </IgTypography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            {googleIntegration?.enabled ? (
              <Grid container spacing={2}>
                <Grid item>
                  <IgButton
                    variant={"contained"}
                    loading={putGaIntegration.isPending}
                    onClick={onSaveClick}
                  >
                    Save
                  </IgButton>
                </Grid>
                <Grid item>
                  <IgButton
                    variant={"contained"}
                    loading={putGaIntegration.isPending}
                    onClick={onResetClick}
                  >
                    Reset Integration
                  </IgButton>
                </Grid>
              </Grid>
            ) : (
              <Box
                onClick={() => onEnableClick()}
                sx={{
                  "& svg": {
                    height: "40px",
                    width: "179px",
                  },

                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
              >
                <SignInWithGoogle />
              </Box>
            )}
          </Grid>
        </Grid>

        {googleIntegration?.enabled ? (
          <>
            <Grid item>
              <IgDivider spacing={"s"} />
            </Grid>
            <GoogleAnalyticsStatusSection
              globalSettings={globalSettings}
              setGlobalSettings={setGlobalSettings}
              googleIntegration={googleIntegration}
            />
          </>
        ) : (
          <GoogleAnalyticsDisclaimer />
        )}
      </Grid>
    </IgPaper>
  );
};
